import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { Bill } from '../model/bill.model';
import { Customer } from '../model/customer.model';
import { ApiService } from '../services/api.service';

@Component({
  selector: 'app-date-change-bills-dialog',
  templateUrl: './date-change-bills-dialog.component.html',
  styleUrls: ['./date-change-bills-dialog.component.scss'],
})
export class DateChangeBillsDialogComponent implements OnInit {
  customer: Customer = null;
  bills: Bill[] = [];
  isLoading = false;
  isSaving = false;
  searchBillDate = '';
  searchPaidDate = '';
  selectedBillNumbers = new Set<number>();
  displayedColumns: string[] = [
    'select',
    'billNumber',
    'billDate',
    'billPaidDate',
    'billPaidStatus',
    'actions',
  ];
  dataSource = new MatTableDataSource<Bill>([]);

  constructor(
    public dialogRef: MatDialogRef<DateChangeBillsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { customer?: Customer; bills?: Bill[]; title?: string },
    private apiService: ApiService,
    private _snackBar: MatSnackBar
  ) {
    this.customer = data.customer || null;
    if (data.bills) {
      this.bills = data.bills;
    }
  }

  ngOnInit(): void {
    if (this.customer) {
      this.loadBills();
    } else {
      this.selectedBillNumbers.clear();
      this.filterBills();
    }
  }

  loadBills(): void {
    this.isLoading = true;
    this.apiService.getCurrentCycleBills(this.customer.customerId).subscribe(
      (res: any) => {
        this.bills = res.bills || [];
        this.selectedBillNumbers.clear();
        this.filterBills();
        this.isLoading = false;
      },
      () => {
        this.isLoading = false;
      }
    );
  }

  filterBills(): void {
    const byBill = this.searchBillDate ? (b: Bill) => b.billDate === this.searchBillDate : () => true;
    const byPaid = this.searchPaidDate ? (b: Bill) => b.billPaidDate === this.searchPaidDate : () => true;
    this.dataSource.data = this.bills.filter(b => byBill(b) && byPaid(b));
  }

  isAllSelected(): boolean {
    return this.dataSource.data.length > 0 && this.dataSource.data.every(b => this.selectedBillNumbers.has(b.billNumber));
  }

  toggleAll(checked: boolean): void {
    this.dataSource.data.forEach(b => {
      if (checked) {
        this.selectedBillNumbers.add(b.billNumber);
      } else {
        this.selectedBillNumbers.delete(b.billNumber);
      }
    });
  }

  isSelected(bill: Bill): boolean {
    return this.selectedBillNumbers.has(bill.billNumber);
  }

  toggleSelection(bill: Bill): void {
    if (this.selectedBillNumbers.has(bill.billNumber)) {
      this.selectedBillNumbers.delete(bill.billNumber);
    } else {
      this.selectedBillNumbers.add(bill.billNumber);
    }
  }

  getSelectedBills(): Bill[] {
    return this.bills.filter(b => this.selectedBillNumbers.has(b.billNumber));
  }

  save(bill: Bill): void {
    const body = {
      billDate: bill.billDate,
      billPaidDate: bill.billPaidDate,
    };
    this.apiService.updateBillDates(bill.billNumber, body).subscribe(
      (res: any) => {
        if (res && res.isSuccess) {
          this._snackBar.open('Bill dates updated', 'Close', { duration: 2000 });
        } else {
          this._snackBar.open('Update failed', 'Close', { duration: 2000 });
        }
      },
      () => {
        this._snackBar.open('Update failed', 'Close', { duration: 2000 });
      }
    );
  }

  saveSelected(): void {
    const selected = this.getSelectedBills();
    if (!selected.length) {
      this._snackBar.open('No bills selected', 'Close', { duration: 2000 });
      return;
    }
    const confirmed = window.confirm(`Update ${selected.length} selected bill(s)?`);
    if (!confirmed) {
      return;
    }
    const payload = selected.map(b => ({
      billNumber: b.billNumber,
      billDate: b.billDate,
      billPaidDate: b.billPaidDate,
    }));
    this.isSaving = true;
    this.apiService.updateBillsDates(payload).subscribe(
      (res: any) => {
        this.isSaving = false;
        if (res && res.isSuccess) {
          this._snackBar.open('Selected bill dates updated', 'Close', { duration: 2000 });
          this.loadBills();
        } else {
          this._snackBar.open('Bulk update failed', 'Close', { duration: 2000 });
        }
      },
      () => {
        this.isSaving = false;
        this._snackBar.open('Bulk update failed', 'Close', { duration: 2000 });
      }
    );
  }

  close(): void {
    this.dialogRef.close();
  }
}
